import { useState } from "react";
import { useAvailableFonts } from "@/hooks/useAvailableFonts";

interface FontFieldProps {
  label: string;
  value: string[] | undefined;
  onChange: (value: string[]) => void;
  description?: string;
}

/**
 * Font stack editor with dropdown of available fonts.
 * Shows checkboxes for known fonts + text input for custom fonts.
 */
export function FontField({ label, value, onChange, description }: FontFieldProps) {
  const availableFonts = useAvailableFonts();
  const [showPicker, setShowPicker] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const currentFonts = value ?? [];

  const toggleFont = (font: string) => {
    if (currentFonts.includes(font)) {
      onChange(currentFonts.filter((f) => f !== font));
    } else {
      onChange([...currentFonts, font]);
    }
  };

  return (
    <div className="group/field rounded-radius-sm px-1.5 py-1 transition-colors hover:bg-surface-sunken/50">
      <div className="flex items-center gap-2">
        <div
          className="relative flex w-28 shrink-0 items-center gap-1"
          onMouseEnter={() => { if (description) setShowTip(true); }}
          onMouseLeave={() => setShowTip(false)}
        >
          <span className="truncate text-[11px] text-text-secondary">{label}</span>
          {description && (
            <span className="text-[9px] text-text-muted/40 opacity-0 transition-opacity group-hover/field:opacity-100">?</span>
          )}
          {showTip && description && (
            <div className="absolute left-0 bottom-full z-50 mb-1 w-52 rounded-radius-sm border border-border-subtle bg-surface-overlay px-2.5 py-1.5 text-[10px] leading-relaxed text-text-secondary shadow-md">
              {description}
            </div>
          )}
        </div>
        <div className="flex flex-1 items-center gap-1.5">
          <div
            className="flex h-7 min-w-0 flex-1 cursor-pointer items-center rounded-radius-sm border border-border-subtle bg-surface-base px-2 text-[11px] text-text-primary"
            onClick={() => setShowPicker(!showPicker)}
          >
            {currentFonts.length > 0 ? (
              <span className="truncate">{currentFonts.join(", ")}</span>
            ) : (
              <span className="text-text-muted">Select fonts...</span>
            )}
          </div>
        </div>
      </div>

      {showPicker && (
        <div className="mt-1.5 ml-28 rounded-radius-sm border border-border-subtle bg-surface-raised p-2 shadow-md">
          <div className="max-h-40 space-y-0.5 overflow-y-auto">
            {availableFonts.map((font) => (
              <label
                key={font}
                className="flex cursor-pointer items-center gap-2 rounded-radius-sm px-1.5 py-1 transition-colors hover:bg-surface-sunken"
              >
                <input
                  type="checkbox"
                  checked={currentFonts.includes(font)}
                  onChange={() => toggleFont(font)}
                  className="accent-accent"
                />
                <span className="text-[10px] text-text-primary">{font}</span>
              </label>
            ))}
          </div>
          <div className="mt-2 border-t border-border-subtle pt-2">
            <div className="text-[9px] text-text-muted mb-1">Custom font (type and press Enter)</div>
            <input
              type="text"
              placeholder="Font Name Regular"
              className="h-6 w-full rounded-radius-sm border border-border-subtle bg-surface-base px-2 text-[10px] text-text-primary focus:border-accent focus:outline-none"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const val = (e.target as HTMLInputElement).value.trim();
                  if (val && !currentFonts.includes(val)) {
                    onChange([...currentFonts, val]);
                    (e.target as HTMLInputElement).value = "";
                  }
                }
              }}
            />
          </div>
          <div className="mt-2 text-[8px] text-text-muted">
            Order matters — first font is primary, others are fallbacks
          </div>
        </div>
      )}
    </div>
  );
}
